/**
 * The part of the Better Auth browser client (`@dcs/api/auth`) the app uses: the session store
 * (`useSession`), email OTP, Google and sign-out. Screens and tests depend on this shape, not on the
 * full client, so a test passes a small fake and the real client stays in its own lazy chunk.
 *
 * Every call resolves `{ error }` (Better Auth never throws for an HTTP error): `error.status` is the
 * HTTP status and `error.code` Better Auth's code (`INVALID_OTP`, `OTP_EXPIRED`...). Answers from the
 * API's own middleware (429 of the OTP rate limit) carry `{ error: { code, message } }` instead, so their
 * `code` is not at the top level: callers look at `status` first.
 */

export interface AuthCallError {
  readonly status: number;
  readonly code?: string | undefined;
  readonly message?: string | undefined;
}

export interface AuthCallResult {
  readonly error: AuthCallError | null;
}

export interface SessionUser {
  readonly id: string;
  readonly email: string;
  readonly name?: string | null | undefined;
}

/** Value of the Better Auth session store (a nanostores atom). */
export interface SessionSnapshot {
  readonly data: { readonly user: SessionUser } | null;
  /** `true` until the first `get-session` answer. */
  readonly isPending: boolean;
  readonly error?: unknown;
  /** Better Auth: fetches `get-session` again and updates the store. */
  readonly refetch?: (() => Promise<unknown>) | undefined;
}

/** nanostores `Atom`: `subscribe` calls the listener at once, then on every change. */
export interface SessionStore {
  get(): SessionSnapshot;
  subscribe(listener: (value: SessionSnapshot) => void): () => void;
}

export interface AuthClientLike {
  readonly useSession: SessionStore;
  readonly emailOtp: {
    sendVerificationOtp(input: { email: string; type: 'sign-in' }): Promise<AuthCallResult>;
  };
  readonly signIn: {
    emailOtp(input: { email: string; otp: string }): Promise<AuthCallResult>;
    social(input: {
      provider: 'google';
      callbackURL: string;
      errorCallbackURL: string;
    }): Promise<AuthCallResult>;
  };
  signOut(): Promise<AuthCallResult>;
}

/** Resolves the auth client; the default one imports `@dcs/api/auth` on first use. */
export type LoadAuthClient = () => Promise<AuthClientLike>;

/**
 * Real client, created once. `@dcs/api/auth` (better-auth/client + plugins) is a dynamic import so it
 * never lands in the shell or the `/ly-thuyet` chunks. Better Auth lives on `/api/auth/*` of the page's
 * own origin (the Pages proxy forwards it to the Worker), which keeps its cookie first-party.
 */
let shared: Promise<AuthClientLike> | null = null;

export const loadAuthClient: LoadAuthClient = () => {
  shared ??= import('@dcs/api/auth').then(
    ({ createAuthClient }): AuthClientLike =>
      // An explicit same origin: an empty base would make Better Auth look for *_AUTH_URL variables.
      createAuthClient({ baseURL: globalThis.location.origin }),
    (error: unknown) => {
      // A failed chunk load (offline before the service worker cached it) may succeed later.
      shared = null;
      throw error;
    },
  );
  return shared;
};
